import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Check, Error } from '@mui/icons-material';
import {
  getSettings,
  updateSettings,
  testConnection,
  Settings as SettingsType,
  TestConnectionResponse,
} from '../api/client';

const Settings: React.FC = () => {
  const [, setSettings] = useState<SettingsType | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [workspaceGid, setWorkspaceGid] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      setSettings(response.data);
      setApiKey(response.data.asana_api_key || '');
      setWorkspaceGid(response.data.workspace_gid || '');
    } catch (err) {
      setError('Erreur lors du chargement des parametres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await updateSettings({
        asana_api_key: apiKey,
        workspace_gid: workspaceGid,
      });
      setSuccess('Parametres sauvegardes avec succes');
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError('');
    setSuccess('');
    setTestResult(null);

    // Save first before testing
    setSaving(true);
    try {
      await updateSettings({
        asana_api_key: apiKey,
        workspace_gid: workspaceGid,
      });
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      setSaving(false);
      return;
    }
    setSaving(false);

    setTesting(true);
    try {
      const response = await testConnection();
      setTestResult(response.data);
      if (response.data.success) {
        setSuccess('Connexion reussie!');
      }
    } catch (err) {
      setError('Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Parametres
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuration Asana API
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Personal Access Token"
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            margin="normal"
            helperText="Obtenez votre token sur https://app.asana.com/0/developer-console"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {testResult?.workspaces && testResult.workspaces.length > 0 && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Workspace</InputLabel>
              <Select
                value={workspaceGid}
                label="Workspace"
                onChange={(e) => setWorkspaceGid(e.target.value)}
              >
                {testResult.workspaces.map((ws) => (
                  <MenuItem key={ws.gid} value={ws.gid}>
                    {ws.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {workspaceGid && !testResult?.workspaces && (
            <TextField
              fullWidth
              label="Workspace GID"
              value={workspaceGid}
              onChange={(e) => setWorkspaceGid(e.target.value)}
              margin="normal"
              disabled
            />
          )}

          {testResult && (
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <Check /> : <Error />}
              sx={{ mt: 2 }}
            >
              {testResult.message}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={!apiKey || testing || saving}
            >
              {testing ? <CircularProgress size={20} /> : 'Tester la connexion'}
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!apiKey || !workspaceGid || saving}
            >
              {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;
